/* src/modules/office/components/MyOfficeCalendar.js */
import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import DeviationRequestModal from './DeviationRequestModal';
import { getDayName, isWeekend, formatDateLocal } from '../utils/dateHelpers'; // ✅ Import new helper

export default function MyOfficeCalendar({ userProfile, appId }) {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [schedules, setSchedules] = useState([]);
    const [deviations, setDeviations] = useState([]);
    const [holidays, setHolidays] = useState([]); 
    const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);

    // Fetch Data
    useEffect(() => {
        const fetchData = async () => {
            // 1. Get Schedules
            const schedRef = collection(db, `artifacts/${appId}/office_schedules`);
            const qSched = query(schedRef, where('assignedUsers', 'array-contains', userProfile.id));
            const schedSnap = await getDocs(qSched);
            setSchedules(schedSnap.docs.map(d => d.data()));

            // 2. Get Deviations
            const devRef = collection(db, `artifacts/${appId}/schedule_deviations`);
            const qDev = query(devRef, where('userId', '==', userProfile.id));
            const devSnap = await getDocs(qDev);
            setDeviations(devSnap.docs.map(d => ({ id: d.id, ...d.data() })));

            // 3. Get Holidays
            const holRef = collection(db, `artifacts/${appId}/public/data/holidays`);
            const holSnap = await getDocs(holRef);
            const allHolidays = holSnap.docs.map(d => {
                const data = d.data();
                
                // ✅ FIX: Force UTC processing for Holidays to ensure "Jan 1" is always "Jan 1"
                // This prevents the holiday from shifting if the user is in a different timezone than the server
                let dateStr = '';
                if (data.date) {
                    const d = data.date.toDate();
                    // We use UTC methods here because Holidays are usually stored as UTC Midnight
                    dateStr = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
                }

                return {
                    ...data,
                    dateStr: dateStr
                };
            });
            setHolidays(allHolidays);
        };
        fetchData();
    }, [appId, userProfile.id]);

    const getHolidayForDay = (dateStr) => {
        const userLoc = userProfile.location ? userProfile.location.toLowerCase().trim() : '';
        const fallbackLoc = 'mumbai';

        const daysHolidays = holidays.filter(h => h.dateStr === dateStr);
        if (daysHolidays.length === 0) return null;

        let match = daysHolidays.find(h => h.location && h.location.toLowerCase() === userLoc);
        if (!match) match = daysHolidays.find(h => h.location && h.location.toLowerCase() === fallbackLoc);
        if (!match) match = daysHolidays.find(h => !h.location || h.location.toLowerCase() === 'all');

        return match;
    };

    const getStatusForDay = (date) => {
        // ✅ FIX: Use local formatter instead of toISOString
        const dateStr = formatDateLocal(date);
        
        // 1. Check Holidays
        const holiday = getHolidayForDay(dateStr);
        if (holiday) return { type: 'Holiday', label: holiday.holidayName, source: 'Public' };

        // 2. Check Deviations
        const deviation = deviations.find(d => d.date === dateStr && d.status === 'Approved');
        if (deviation) return { type: deviation.type, source: 'Ad-hoc' };

        // 3. Check Compensatory Dates
        const comp = deviations.find(d => d.compensatoryDate === dateStr && d.status === 'Approved');
        if (comp) return { type: 'WFO', source: 'Compensatory' };

        // 4. Check Scheduled Plans
        const applicableSched = schedules
            .filter(s => {
                const start = new Date(s.startDate);
                const end = new Date(s.endDate);
                // Compare using string to avoid time issues
                return dateStr >= s.startDate && dateStr <= s.endDate;
            })
            .sort((a, b) => new Date(b.startDate) - new Date(a.startDate))[0];

        if (applicableSched) {
            const dayName = getDayName(date);
            return { type: applicableSched.pattern[dayName] || 'WFH', source: 'Planned' };
        }

        // 5. No Schedule Found
        return { type: 'Unassigned', source: 'None' };
    };

    const handleDayClick = (date, status) => {
        if (status.type === 'Holiday') return;
        setSelectedDate(date);
        setIsRequestModalOpen(true);
    };

    const renderCalendarGrid = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const firstDay = new Date(year, month, 1).getDay(); 
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        
        const offset = firstDay === 0 ? 6 : firstDay - 1;
        const slots = [];

        for (let i = 0; i < offset; i++) slots.push(<div key={`empty-${i}`} className="h-28 bg-gray-50/30 border border-transparent"></div>);

        for (let d = 1; d <= daysInMonth; d++) {
            const date = new Date(year, month, d);
            const status = getStatusForDay(date);
            const isWeekendDay = isWeekend(date);
            const isToday = date.toDateString() === new Date().toDateString();

            let bgClass = 'bg-white';
            let borderClass = 'border-gray-100';
            let labelClass = 'hidden';
            let labelText = '';

            if (status.type === 'Holiday') {
                bgClass = 'bg-red-50';
                labelClass = 'bg-red-100 text-red-700 border-red-200';
                labelText = `🎉 ${status.label}`;
            } else if (status.type === 'WFO') {
                labelClass = 'bg-blue-100 text-blue-700 border-blue-200';
                labelText = 'Office';
            } else if (status.type === 'WFH') {
                labelClass = 'bg-green-100 text-green-700 border-green-200';
                labelText = 'Home';
            } else if (status.type === 'Unassigned') {
                bgClass = 'bg-gray-50'; 
                labelClass = 'text-gray-400 text-[10px] italic border border-transparent px-0';
                labelText = 'No Schedule';
            }

            if (isToday) {
                borderClass = 'border-[#20BEC6] border-2 shadow-md z-10'; 
            }

            slots.push(
                <div 
                    key={d} 
                    onClick={() => !isWeekendDay && status.type !== 'Holiday' && handleDayClick(date, status)}
                    className={`h-28 p-2 relative rounded-lg transition group border flex flex-col justify-between
                        ${bgClass} ${borderClass}
                        ${!isWeekendDay && status.type !== 'Holiday' ? 'hover:shadow-lg cursor-pointer' : ''}
                    `}
                >
                    <div className="flex justify-between items-start">
                        <span className={`text-sm font-bold ${isWeekendDay ? 'text-gray-400' : 'text-gray-700'} ${isToday ? 'text-[#20BEC6]' : ''}`}>
                            {d}
                        </span>
                        {isToday && <span className="text-[9px] font-bold bg-[#20BEC6] text-white px-1.5 rounded">TODAY</span>}
                    </div>
                    
                    {!isWeekendDay && (
                        <div className="mt-1">
                            <div className={`text-xs px-2 py-1 rounded border text-center font-medium truncate ${labelClass}`} title={labelText}>
                                {labelText}
                            </div>
                            
                            {status.source === 'Ad-hoc' && (
                                <div className="text-[9px] text-purple-600 text-center mt-0.5 font-medium">*Deviation</div>
                            )}
                            {status.source === 'Compensatory' && (
                                <div className="text-[9px] text-orange-600 text-center mt-0.5 font-medium">*Compensatory</div>
                            )}
                        </div>
                    )}
                </div>
            );
        }
        return slots;
    };

    return (
        <div>
            {/* Warning Banner if Unassigned */}
            {schedules.length === 0 && (
                <div className="mb-6 bg-yellow-50 border border-yellow-200 p-3 rounded-lg flex items-center text-yellow-800 text-sm">
                    <span className="mr-2">⚠️</span>
                    <span>No schedule assigned. Please contact your manager to set your work plan.</span>
                </div>
            )}

            {/* Calendar Controls */}
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <h3 className="text-xl font-bold text-[#213368]">
                    {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </h3>
                <div className="flex space-x-2">
                    <button onClick={() => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() - 1)))} className="p-2 bg-white border border-gray-200 hover:bg-gray-50 rounded shadow-sm text-gray-600 font-bold">&lt;</button>
                    <button onClick={() => setCurrentDate(new Date())} className="px-4 py-2 text-sm bg-white border border-gray-200 rounded shadow-sm hover:bg-gray-50 font-bold text-[#213368]">This Month</button>
                    <button onClick={() => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() + 1)))} className="p-2 bg-white border border-gray-200 hover:bg-gray-50 rounded shadow-sm text-gray-600 font-bold">&gt;</button>
                </div>
            </div>

            <div className="grid grid-cols-7 gap-2 mb-2">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                    <div key={day} className="text-center text-xs font-bold text-gray-400 uppercase tracking-wider">{day}</div>
                ))}
            </div>
            <div className="grid grid-cols-7 gap-2">
                {renderCalendarGrid()}
            </div>

            {isRequestModalOpen && (
                <DeviationRequestModal 
                    appId={appId} 
                    userProfile={userProfile}
                    date={selectedDate}
                    currentStatus={getStatusForDay(selectedDate)}
		    checkDateStatus={getStatusForDay}
                    onClose={() => setIsRequestModalOpen(false)}
                />
            )}
        </div>
    );
}